import React, { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { FaBuilding, FaUsers, FaSyncAlt, FaExclamationCircle, FaPlus, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import Select from 'react-select'
import 'react-toastify/dist/ReactToastify.css'
import './Departments.css'
import API_BASE_URL from '../../../config/api'

const Departments = () => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [costCenters, setCostCenters] = useState([])
  const [costCentersLoading, setCostCentersLoading] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editDepartment, setEditDepartment] = useState(null)
  const [editName, setEditName] = useState('')
  const [editCostCenter, setEditCostCenter] = useState(null)
  const [editSaving, setEditSaving] = useState(false)
  const baseUrl = API_BASE_URL

  const fetchDepartments = async () => {
    setLoading(true)
    setError('')
    try {
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/departments/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setDepartments(response.data)
    } catch (err) {
      setError('Failed to fetch departments.')
      toast.error('Failed to fetch departments.')
    } finally {
      setLoading(false)
    }
  }

  const fetchCostCenters = async () => {
    setCostCentersLoading(true)
    try {
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/course-centers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setCostCenters(response.data)
    } catch (err) {
      toast.error('Failed to fetch cost centers.')
    } finally {
      setCostCentersLoading(false)
    }
  }

  const handleEdit = async (department) => {
    await fetchCostCenters()
    setEditDepartment(department)
    setEditName(department.name)
    setEditCostCenter(department.course_center ? { value: department.course_center, label: department.course_center_name } : null)
    setEditModalOpen(true)
  }

  const handleEditSave = async (e) => {
    e.preventDefault()
    setEditSaving(true)
    try {
      const token = Cookies.get('token')
      await axios.put(`${baseUrl}/api/departments/${editDepartment.id}/`, {
        name: editName,
        course_center: editCostCenter?.value
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      toast.success('Department updated successfully!')
      setEditModalOpen(false)
      setEditDepartment(null)
      fetchDepartments()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update department.')
    } finally {
      setEditSaving(false)
    }
  }

  const handleEditClose = () => {
    setEditModalOpen(false)
    setEditDepartment(null)
  }

  const handleDelete = async (department) => {
    if (window.confirm(`Are you sure you want to delete "${department.name}"?`)) {
      try {
        const token = Cookies.get('token')
        await axios.delete(`${baseUrl}/api/departments/${department.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        toast.success('Department deleted successfully!')
        fetchDepartments() // Refresh the list
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete department.')
      }
    }
  }

  useEffect(() => {
    fetchDepartments()
    // eslint-disable-next-line
  }, [])

  const costCenterOptions = costCenters.map(center => ({ value: center.id, label: center.name }))

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="departments-container">
        <div className="page-header">
          <div className="header-content">
            <div className="header-left">
              <div className="page-title">
                <h1>Departments</h1>
                <p>View and manage all your organization's departments</p>
              </div>
            </div>
            <div className="header-right">
              <Link to="/hr/department/add" className="add-button">
                <FaPlus />
                <span>Add Department</span>
              </Link>
              <button className="refresh-button" onClick={fetchDepartments} disabled={loading} title="Refresh">
                <FaSyncAlt className={loading ? 'spin' : ''} />
              </button>
            </div>
          </div>
        </div>

        <div className="departments-content">
          {loading ? (
            <div className="loading-state">
              <FaSyncAlt className="spin" /> Loading departments...
            </div>
          ) : error ? (
            <div className="error-state">
              <FaExclamationCircle /> {error}
            </div>
          ) : departments.length === 0 ? (
            <div className="empty-state">
              <FaBuilding /> No departments found.
            </div>
          ) : (
            <div className="departments-card-grid">
              {departments.map((dept, idx) => (
                <div className="department-card" key={dept.id || idx}>
                  <div className="card-header">
                    <div className="card-icon">
                      <FaBuilding />
                    </div>
                    <div className="card-actions">
                      <button 
                        className="action-button edit-button" 
                        onClick={() => handleEdit(dept)}
                        title="Edit Department"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="action-button delete-button" 
                        onClick={() => handleDelete(dept)}
                        title="Delete Department"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-title">{dept.name} ( {dept.course_center_name} )</div>
                    <div className="card-meta">
                      <span className="card-label"><FaUsers style={{marginRight: 4}}/> Employees:</span> {dept.employee_count !== undefined ? dept.employee_count : (dept.employees ? dept.employees.length : 'N/A')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={handleEditClose} title="Close">
              <FaTimes />
            </button>
            <h2 className="modal-title">Edit Department</h2>
            <form onSubmit={handleEditSave} className="modal-form">
              <div className="form-group">
                <label htmlFor="editName">
                  <span className="label-text">Department Name</span>
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="editName"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  required
                  className="form-input"
                  disabled={editSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editCostCenter">
                  <span className="label-text">Cost Center</span>
                  <span className="required">*</span>
                </label>
                <Select
                  id="editCostCenter"
                  classNamePrefix="react-select"
                  isLoading={costCentersLoading}
                  isDisabled={editSaving || costCentersLoading}
                  options={costCenterOptions}
                  value={editCostCenter}
                  onChange={setEditCostCenter}
                  placeholder={costCentersLoading ? 'Loading cost centers...' : 'Select a cost center'}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '48px',
                      borderRadius: '12px',
                      borderColor: state.isFocused ? '#667eea' : '#e5e7eb',
                      boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : undefined,
                      background: state.isDisabled ? '#f3f4f6' : '#f9fafb',
                      fontSize: '16px',
                      color: '#374151',
                    }),
                    option: (base, state) => ({
                      ...base,
                      background: state.isSelected ? '#667eea' : state.isFocused ? '#e0e7ff' : '#fff',
                      color: state.isSelected ? '#fff' : '#1e293b',
                      fontWeight: state.isSelected ? 600 : 400,
                      fontSize: '16px',
                    }),
                  }}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleEditClose}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={editSaving || !editName.trim() || !editCostCenter}
                >
                  {editSaving ? (
                    <>
                      <FaSave className="spinner" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FaSave />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default Departments
